import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Select, Row, Col, FormInstance, Button } from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';

import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';

import { AccountPayableGroupingFormValues } from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import {
  CompaniesInfiniteSelectField,
  InputSearch,
} from '@/components/_common';
import { Creditor } from '@/core/domain/financial/ForecastPayable/ForecastPayable.types';
import EmployeesSelectedModal from '@/components/_common/EmployeesSelectedModal';
import SuppliersSelectedModal from '@/components/_common/SuppliersSelectedModal';
import { SupplierReduced } from '@/core/domain/people/Suppliers/Suppliers.types';
import { Employee } from '@/core/domain/enterprise/Employees/Employees.types';

interface Props {
  form: FormInstance<AccountPayableGroupingFormValues>;
  onCallback: () => void;
}

function GroupingForm({ form, onCallback }: Props) {
  const intl = useIntl();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const [modalEmployeeVisible, modalEmployeeAction] = useDisclosure();
  const [modalSupplierVisible, modalSupplierAction] = useDisclosure();

  const handleSupplierSelect = (supplier?: SupplierReduced) => {
    form.setFieldsValue({
      supplier: supplier?.name,
      supplierId: supplier?.id,
    });
    modalSupplierAction.close();
  };

  const handleEmployeeSelect = (employee?: Employee) => {
    form.setFieldsValue({
      employee: employee?.name,
      employeeId: employee?.id,
    });
    modalEmployeeAction.close();
  };

  return (
    <>
      <Form layout="vertical" form={form}>
        <Row gutter={[16, 9]}>
          <Col xs={24} sm={24} md={10}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'groupingsAccountPayable.form.company',
              })}
              name="companyId"
              rules={[defaultEmptyFieldError]}
            >
              <CompaniesInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={3} md={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.creditor',
              })}
              name="creditor"
              rules={[defaultEmptyFieldError]}
              initialValue={Creditor.supplier}
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: Creditor.supplier,
                    label: intl.formatMessage({
                      id: 'release.form.supplier',
                    }),
                  },
                  {
                    value: Creditor.employee,
                    label: intl.formatMessage({
                      id: 'release.form.employee',
                    }),
                  },
                ]}
              ></Select>
            </FloatingFormItem>
          </Col>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const creditor = getFieldValue('creditor');

              if (creditor === Creditor.employee) {
                return (
                  <Col span={19} md={11} lg={8} xl={8}>
                    <Form.Item hidden name="employeeId" />

                    <FloatingFormItem
                      name="employee"
                      label={intl.formatMessage({
                        id: 'release.form.employee',
                      })}
                      rules={[defaultEmptyFieldError]}
                    >
                      <InputSearch
                        inputProps={{ disabled: true }}
                        buttonProps={{ onClick: modalEmployeeAction.open }}
                      />
                    </FloatingFormItem>
                  </Col>
                );
              } else {
                return (
                  <Col span={19} md={11} lg={8} xl={8}>
                    <Form.Item hidden name="supplierId" />

                    <FloatingFormItem
                      name="supplier"
                      label={intl.formatMessage({
                        id: 'release.form.supplier',
                      })}
                      rules={[defaultEmptyFieldError]}
                    >
                      <InputSearch
                        inputProps={{ disabled: true }}
                        buttonProps={{ onClick: modalSupplierAction.open }}
                      />
                    </FloatingFormItem>
                  </Col>
                );
              }
            }}
          </Form.Item>

          <Col xs={24} sm={12} md={2}>
            <Button size="large" type="primary" onClick={onCallback}>
              <FormattedMessage id="groupingsAccountPayable.generate.searchAccounts" />
            </Button>
          </Col>
        </Row>
      </Form>

      <EmployeesSelectedModal
        visible={modalEmployeeVisible}
        handleVisible={modalEmployeeAction.toggle}
        noSelection
        onEmployeeSelect={handleEmployeeSelect}
      />

      <SuppliersSelectedModal
        visible={modalSupplierVisible}
        handleVisible={modalSupplierAction.toggle}
        noSelection
        onSupplierSelect={handleSupplierSelect}
      />
    </>
  );
}

export default GroupingForm;
